import PropTypes from "prop-types";
import DialogTitle from "@material-ui/core/DialogTitle";
import DialogContent from "@material-ui/core/DialogContent";
import makeStyles from "@material-ui/core/styles/makeStyles";
import IconButton from "@material-ui/core/IconButton";
import Grid from "@material-ui/core/Grid";
import React, {useEffect} from "react";
import Typography from "@material-ui/core/Typography";
import {useBreakpoints} from "../../../hooks";
import {useI18N} from "../../../common/common";
import OfferOverview from "../../view/marketplace/OfferOverview";
import CustomButton from "../../widget/button/CustomButton";
import CustomAlert from "../../widget/alerts/CustomAlert";
import CloseIcon from "../../widget/streamline-icon/CloseIcon";
import NoIndexDialog from "../noIndex/NoIndexDialog";
import AlertCircle from "@hapinest/theme/icons/streamlinehq/img/streamline-regular/interface-essential/alerts/alert-circle.svg";
import StreamlineIcon from "../../widget/streamline-icon/StreamlineIcon";

const useStyles = makeStyles((theme) => ({
    titlePadding: {
        padding: 0
    },
    contentPadding: {
        paddingLeft: theme.spacing(3),
        paddingRight: theme.spacing(3)
    },
    alert: {
        maxWidth: 625,
        marginBottom: theme.spacing(2),
        "& .MuiAlert-icon": {
            alignItems: "center"
        }
    },
    titles: {
        color: "#000000",
    },
    marginTopTitles: {
        marginTop: theme.spacing(5)
    },
    marginTopBuyerSeller: {
        marginTop: theme.spacing(2)
    },
    rightAlign: {
        textAlign: "end"
    },
    requestDocumentsButton: {
        marginTop: theme.spacing(5),
        marginBottom: theme.spacing(5)
    }
}));

const OfferCheckDialog = ({
    isOpen,
    onClose,
    onButtonClick,
    offer,
    totalPoints,
    buyer,
    seller,
    isTransferDocuments,
    loadingButton,
    error,
    setError,
    showTrialWarning
}) => {
    const classes = useStyles();
    const {i18n} = useI18N();
    const {isExtraSmallScreen} = useBreakpoints();
    const [open, setOpen] = React.useState(false);

    useEffect(() => {
        setOpen(isOpen);
    }, [isOpen]);

    const createBoldFont = (str) => {
        return (
            <span
                key={str}
                style={{fontWeight: "bold"}}
            >
                {str}
            </span>
        );
    };

    const handleClose = () => {
        if (onClose) {
            setError(undefined);
            onClose();
        }
    };

    const handleButtonClick = () => {
        if (onButtonClick) {
            setError(undefined);
            onButtonClick();
        }
    };

    return(
        <NoIndexDialog
            open={open}
            fullScreen={isExtraSmallScreen()}
            fullWidth={!isExtraSmallScreen()}
            maxWidth="sm"
            onClose={handleClose}
            disableBackdropClick
        >
            <DialogTitle className={classes.titlePadding}>
                <Grid
                    container
                    alignItems="center"
                    spacing={0}
                >
                    <Grid
                        item
                        md={12}
                        xs={12}
                        sm={12}
                        align={"right"}
                    >
                        <IconButton
                            aria-label="close"
                            onClick={handleClose}
                        >
                            <CloseIcon/>
                        </IconButton>
                    </Grid>
                </Grid>


            </DialogTitle>

            <DialogContent className={classes.contentPadding} >

                {error && (
                    <CustomAlert
                        className={classes.alert}
                        type={"error"}
                    >
                        <Typography
                            variant="body1"
                            color="textSecondary"
                            className={classes.titles}
                        >
                            {i18n("common.label.generic_error")}
                        </Typography>

                    </CustomAlert>
                )}

                {showTrialWarning && (
                    <CustomAlert
                        className={classes.alert}
                        type="warning"
                        icon={<StreamlineIcon><AlertCircle/></StreamlineIcon>}
                        noTypeText
                    >
                        <Typography
                            variant="body1"
                            color="textSecondary"
                            className={classes.titles}
                        >
                            {i18n("view.marketplace.admin.view.trial_member_warning", {boldFont: createBoldFont})}
                        </Typography>
                    </CustomAlert>
                )}

                <Grid
                    item
                    md={12}
                    xs={12}
                    sm={12}
                >
                    <Typography variant="h2">
                        {i18n("view.marketplace.offer_details.check_sale_dialog.final_check")} {offer?.offer_id}
                    </Typography>

                    <Typography variant="body1">
                        {i18n("view.marketplace.offer_details.check_sale_dialog.final_check_confirmation")}
                    </Typography>
                </Grid>

                <Grid
                    item
                    md={12}
                    xs={12}
                    sm={12}
                    className={classes.marginTopTitles}
                >
                    <OfferOverview
                        points={offer?.points}
                        shares={offer?.shares}
                        totalPoints={totalPoints}
                        sharesPrice={offer?.shares?.[0]?.share_price}
                        pointsPrice={offer?.points?.[0]?.price_per_point}
                        offerTotalPrice={offer?.total_price}
                        showGTC={false}
                    />
                </Grid>

                <Grid container>
                    {isTransferDocuments && seller && (
                        <Grid
                            item
                            md={6}
                            xs={6}
                            sm={6}
                            className={classes.marginTopTitles}
                        >
                            <Typography variant="subtitle1">
                                {i18n("view.marketplace.offer_details.check_sale_dialog.seller_details")}
                            </Typography>

                            <Typography
                                variant="body1"
                                className={classes.marginTopBuyerSeller}
                            >
                                {seller.hapimag_member_id}
                            </Typography>

                            <Typography
                                variant="body1"
                                className={classes.marginTopBuyerSeller}
                            >
                                {`${seller.firstname} ${seller.lastname}`}
                            </Typography>

                            <Typography
                                variant="body1"
                                className={classes.marginTopBuyerSeller}
                            >
                                {seller?.email}
                            </Typography>

                        </Grid>
                    )}

                    <Grid
                        item
                        md={6}
                        xs={6}
                        sm={6}
                        className={classes.marginTopTitles}
                    >
                        <Typography variant="subtitle1">
                            {i18n("view.marketplace.offer_details.check_sale_dialog.buyer_details")}
                        </Typography>

                        <Typography
                            variant="body1"
                            className={classes.marginTopBuyerSeller}
                        >
                            {buyer?.hapimag_member_id}
                        </Typography>

                        <Typography
                            variant="body1"
                            className={classes.marginTopBuyerSeller}
                        >
                            {`${buyer?.firstname} ${buyer?.lastname}`}
                        </Typography>

                        <Typography
                            variant="body1"
                            className={classes.marginTopBuyerSeller}
                        >
                            {buyer?.email}
                        </Typography>

                    </Grid>
                </Grid>

                <Grid container>
                    <Grid
                        item
                        md={12}
                        sm={12}
                        xs={12}
                        className={classes.rightAlign}
                    >
                        <CustomButton
                            variant={"contained"}
                            disableElevation
                            size={"large"}
                            className={classes.requestDocumentsButton}
                            onClick={handleButtonClick}
                            loading={loadingButton}
                            e2e-id={"request-documents-final-btn"}
                        >
                            {isTransferDocuments ? i18n( "view.marketplace.admin.view.transfer_shares") : i18n( "view.marketplace.offer_details.request_documents")}
                        </CustomButton>

                    </Grid>

                </Grid>
            </DialogContent>
        </NoIndexDialog>
    );
};

OfferCheckDialog.defaultProps = {
    loadingButton: false,
    showTrialWarning: false
};

OfferCheckDialog.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onButtonClick: PropTypes.func.isRequired,
    isTransferDocuments: PropTypes.bool,
    offer: PropTypes.object,
    totalPoints: PropTypes.number,
    buyer: PropTypes.object,
    seller: PropTypes.object,
    loadingButton: PropTypes.bool,
    error: PropTypes.object,
    setError: PropTypes.func,
    showTrialWarning: PropTypes.bool
};

export default OfferCheckDialog;
